const { getFriendPayConfig, calculateTimeRemaining, formatExpirationDate,
      formatCountdownTime,
      formatDaysHours,
      getTimerState } = require('../utils/friendPayUtils');
const { handleShare } = require('../utils/share');
const {
  FRIEND_PAY_SELECTORS,
  FRIEND_PAY_CLASSES,
  FRIEND_PAY_TIMING
} = require('../constant/friendPayConstants');
const { mainConfigService } = require('../services/config');
const { $t } = require('../translate');
const { escapeHtml } = require('../security');
const { timerExpiredHtml, timerMoreThanDayHtml, timerLessThanDayHtml } = require('../html');

class FriendPayService {
  constructor(documentCheckout) {
    this.documentCheckout = documentCheckout;
    this.timerInterval = null;
    this.currentSheet = null; // Track active sheet: 'main' or 'send'
  }

  /**
   * Get configuration for friend pay feature
   */
  getConfig() {
    return getFriendPayConfig();
  }

  /**
   * Show a sheet with animation (private method)
   * @private
   * @param {string} sheetId - Sheet selector ID
   * @param {string} htmlContent - Sheet HTML content
   * @param {Function} setupCallback - Callback to setup event listeners
   */
  _showSheet(sheetId, htmlContent, setupCallback) {
    let sheetContainer = this.documentCheckout.querySelector(sheetId);

    if (!sheetContainer) {
      this.documentCheckout.querySelector('.ottu__sdk-main').insertAdjacentHTML('afterend', htmlContent);
      sheetContainer = this.documentCheckout.querySelector(sheetId);
    }

    // Use requestAnimationFrame to ensure the browser paints the initial state
    // before adding the active class that triggers the animation
    requestAnimationFrame(() => {
      sheetContainer.classList.add(FRIEND_PAY_CLASSES.SHEET_ACTIVE);
    });

    if (setupCallback) {
      setupCallback(sheetContainer);
    }

    this.currentSheet = sheetId;
  }

  /**
   * Close a sheet with animation
   * @param {string} sheetId - Sheet selector ID
   */
  closeSheet(sheetId) {
    const sheetContainer = this.documentCheckout.querySelector(sheetId);

    if (sheetContainer) {
      // Remove active class to trigger slide-down animation
      // CSS handles the visibility delay automatically (0.8s)
      sheetContainer.classList.remove(FRIEND_PAY_CLASSES.SHEET_ACTIVE);
    }

    // Clean up timer if closing main sheet
    if (sheetId === FRIEND_PAY_SELECTORS.MAIN_SHEET && this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    this.currentSheet = null;
  }

  /**
   * Setup common event listeners for sheets (close button, overlay click)
   * @param {HTMLElement} sheetContainer - Sheet container element
   * @param {Function} closeCallback - Callback to execute when closing
   */
  setupCommonListeners(sheetContainer, closeCallback) {
    // Close button
    const closeBtn = sheetContainer.querySelector(FRIEND_PAY_SELECTORS.CLOSE_BTN);
    if (closeBtn && !closeBtn.dataset.listenerAdded) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closeCallback();
      });
      closeBtn.dataset.listenerAdded = 'true';
    }

    // Overlay click - close when clicking outside content
    const overlay = sheetContainer.querySelector(FRIEND_PAY_SELECTORS.OVERLAY);
    if (overlay && !overlay.dataset.listenerAdded) {
      overlay.addEventListener('click', (e) => {
        // Only close if clicking directly on the overlay (not on content)
        if (e.target === overlay) {
          closeCallback();
        }
      });
      overlay.dataset.listenerAdded = 'true';
    }
  }

  /**
   * Start countdown timer with three display states
   * @param {HTMLElement} sheetContainer - Sheet container with countdown element
   */
  startCountdown(sheetContainer) {
    const config = this.getConfig();
    const expirationTime = config.expirationTime;

    if (!expirationTime) return;

    const timerContainer = sheetContainer.querySelector('.ottu__sdk-friend-pay-timer-container');
    if (!timerContainer) return;

    const expirationDate = new Date(expirationTime);

    // Validate that expirationDate is a valid Date object
    if (isNaN(expirationDate.getTime())) {
      console.warn('[FriendPay] Invalid expiration time:', expirationTime);
      return;
    }

    const currentLang = mainConfigService.get('lang') || 'en';

    let currentState = null;

    const updateCountdown = () => {
      const now = new Date();
      const remainingMs = expirationDate - now;
      const newState = getTimerState(remainingMs);

      // Update HTML structure when state changes
      if (newState !== currentState) {
        currentState = newState;
        this._updateTimerHTML(sheetContainer, timerContainer, newState, expirationDate, currentLang);
      }

      // Update countdown content based on current state
      if (newState === 'expired') {
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
        return;
      } else if (newState === 'more_than_day') {
        const timeComponents = calculateTimeRemaining(remainingMs);
        const countdownElement = timerContainer.querySelector('.ottu__sdk-friend-pay-countdown');
        if (countdownElement) {
          countdownElement.textContent = formatDaysHours(timeComponents.days, timeComponents.hours, currentLang);
        }
      } else {
        const timeComponents = calculateTimeRemaining(remainingMs);
        const countdownElement = timerContainer.querySelector('.ottu__sdk-friend-pay-countdown');
        if (countdownElement) {
          countdownElement.textContent = formatCountdownTime(timeComponents);
        }
      }
    };

    // Run initial update
    updateCountdown();

    // Only set interval if timer hasn't expired
    if (this.timerInterval === null) {
      // Check if we're not in expired state before setting interval
      const now = new Date();
      const remainingMs = expirationDate - now;
      const initialState = getTimerState(remainingMs);

      if (initialState !== 'expired') {
        this.timerInterval = setInterval(updateCountdown, FRIEND_PAY_TIMING.TIMER_INTERVAL_MS);
      }
    }
  }

  /**
   * Toggle visibility of sections and button states when timer expires
   * @private
   * @param {HTMLElement} sheetContainer - Sheet container element
   * @param {boolean} isExpired - Whether the timer is expired
   */
  _toggleTimerSections(sheetContainer, isExpired) {
    // Toggle info hint section
    const infoHint = sheetContainer.querySelector('#ottu__sdk-friend-pay-info-hint');
    if (infoHint) {
      infoHint.style.display = isExpired ? 'none' : '';
    }

    // Toggle buttons state
    const sendBtn = sheetContainer.querySelector('#ottu__sdk-friend-pay-send-btn');
    const changeBtn = sheetContainer.querySelector('#ottu__sdk-friend-pay-change-btn');

    if (sendBtn) {
      sendBtn.disabled = isExpired;
      sendBtn.style.opacity = isExpired ? '0.5' : '';
      sendBtn.style.cursor = isExpired ? 'not-allowed' : '';
    }

    if (changeBtn) {
      changeBtn.disabled = isExpired;
      changeBtn.style.opacity = isExpired ? '0.5' : '';
      changeBtn.style.cursor = isExpired ? 'not-allowed' : '';
    }

    // Toggle information section
    const infoSection = sheetContainer.querySelector('#ottu__sdk-friend-pay-info-section');
    if (infoSection) {
      infoSection.style.display = isExpired ? 'none' : '';
    }

    // Toggle "Already paid? Update" section
    const updateLink = sheetContainer.querySelector('#ottu__sdk-friend-pay-update-link');
    if (updateLink) {
      updateLink.style.display = isExpired ? 'none' : '';
    }
  }

  /**
   * Update timer HTML based on state
   * @private
   * @param {HTMLElement} sheetContainer - Sheet container element
   * @param {HTMLElement} timerContainer - Timer container element
   * @param {string} state - Timer state: 'expired' | 'less_than_day' | 'more_than_day'
   * @param {Date} expirationDate - Expiration date object
   * @param {string} lang - Language code
   */
  _updateTimerHTML(sheetContainer, timerContainer, state, expirationDate, lang) {
    // Update sheet heading based on state
    const sheetHeading = sheetContainer.querySelector('#ottu__sdk-friend-pay-sheet-heading');
    if (sheetHeading) {
      if (state === 'expired') {
        sheetHeading.textContent = $t('Payment Link Expired');
      } else {
        sheetHeading.textContent = $t('Ask a friend to pay to complete payment');
      }
    }

    if (state === 'expired') {
      // Expired state: use timerExpiredHtml
      const title = escapeHtml($t('Payment Link Expired'));
      const message = escapeHtml($t('The payment link has expired and your friend can no longer complete the payment.'));

      timerContainer.innerHTML = timerExpiredHtml(title, message);
      timerContainer.classList.add('ottu__sdk-friend-pay-timer-expired', 'ottu__sdk-friend-pay-timer-expired-theme');

      // Hide sections and disable buttons
      this._toggleTimerSections(sheetContainer, true);

    } else if (state === 'more_than_day') {
      // More than 1 day: use timerMoreThanDayHtml
      const label = escapeHtml($t('Please complete payment within'));
      const expiresLabel = escapeHtml($t('Expires on'));
      const expirationDateStr = escapeHtml(formatExpirationDate(expirationDate, lang));
      const placeholder = escapeHtml($t('days'));

      timerContainer.innerHTML = timerMoreThanDayHtml(label, placeholder, expiresLabel, expirationDateStr);
      timerContainer.classList.remove('ottu__sdk-friend-pay-timer-expired', 'ottu__sdk-friend-pay-timer-expired-theme');

      // Show sections and enable buttons
      this._toggleTimerSections(sheetContainer, false);

    } else {
      // Less than 1 day: use timerLessThanDayHtml
      const label = escapeHtml($t('Please complete payment within'));

      timerContainer.innerHTML = timerLessThanDayHtml(label);
      timerContainer.classList.remove('ottu__sdk-friend-pay-timer-expired', 'ottu__sdk-friend-pay-timer-expired-theme');

      // Show sections and enable buttons
      this._toggleTimerSections(sheetContainer, false);
    }
  }

  /**
   * Handle share action
   * Delegates to the share utility for platform-specific sharing
   * @param {string} shareType - Platform type (whatsapp, messenger, messages, email, facebook, share, copy)
   * @param {string} message - Custom message to share
   */
  handleShare(shareType, message) {
    const config = this.getConfig();
    const shareMessage = message || config.shareText;

    handleShare(shareType, {
      url: window.location.href,
      message: shareMessage
    });
  }

  /**
   * Show the main Friend Pay sheet with full event handling
   * @param {string} htmlContent - Sheet HTML content from friendPaySheetModal()
   * @param {Object} handlers - Required handler functions for cross-class operations
   * @param {Function} handlers.onClose - Called when user closes the sheet
   * @param {Function} handlers.onSendToFriend - Called when user clicks "Send to Friend"
   * @param {Function} handlers.onChangePayment - Called when user clicks "Change Payment"
   * @param {Function} handlers.onUpdate - Async function to handle payment status update
   */
  showMainSheet(htmlContent, handlers) {
    this._validateHandlers(handlers, ['onClose', 'onSendToFriend', 'onChangePayment', 'onUpdate']);

    this._showSheet(FRIEND_PAY_SELECTORS.MAIN_SHEET, htmlContent, (sheetContainer) => {
      this.setupCommonListeners(sheetContainer, handlers.onClose);
      this._setupMainSheetActions(sheetContainer, handlers);
      this.startCountdown(sheetContainer);
    });
  }

  /**
   * Show the send Friend Pay sheet with full event handling
   * @param {string} htmlContent - Sheet HTML content from friendPaySendSheet()
   * @param {Object} handlers - Required handler functions
   * @param {Function} handlers.onClose - Called when user closes the sheet
   */
  showSendSheet(htmlContent, handlers) {
    this._validateHandlers(handlers, ['onClose']);

    this._showSheet(FRIEND_PAY_SELECTORS.SEND_SHEET, htmlContent, (sendSheetContainer) => {
      this.setupCommonListeners(sendSheetContainer, handlers.onClose);
      this._setupTextareaCounter(sendSheetContainer);
      this._setupChangeMessageLink(sendSheetContainer);
      this._setupShareButtons(sendSheetContainer);
    });
  }

  /**
   * Validate that all required handlers are provided
   * @private
   * @param {Object} handlers - Handler object to validate
   * @param {string[]} required - Array of required handler names
   * @throws {Error} If any required handler is missing or not a function
   */
  _validateHandlers(handlers, required) {
    if (!handlers || typeof handlers !== 'object') {
      throw new Error('FriendPayService: handlers object is required');
    }

    required.forEach(handlerName => {
      if (typeof handlers[handlerName] !== 'function') {
        throw new Error(`FriendPayService: ${handlerName} handler is required and must be a function`);
      }
    });
  }

  /**
   * Setup textarea character counter
   * @private
   * @param {HTMLElement} container - Sheet container element
   */
  _setupTextareaCounter(container) {
    const textarea = container.querySelector(FRIEND_PAY_SELECTORS.TEXTAREA);
    const charCount = container.querySelector(FRIEND_PAY_SELECTORS.CHAR_COUNT);

    if (textarea && charCount && !textarea.dataset.listenerAdded) {
      textarea.addEventListener('input', () => {
        charCount.textContent = textarea.value.length;
      });
      textarea.dataset.listenerAdded = 'true';
    }
  }

  /**
   * Setup "Change Message" link to clear textarea
   * @private
   * @param {HTMLElement} container - Sheet container element
   */
  _setupChangeMessageLink(container) {
    const textarea = container.querySelector(FRIEND_PAY_SELECTORS.TEXTAREA);
    const changeMessageLink = container.querySelector('.ottu__sdk-friend-pay-change-message');
    const charCount = container.querySelector(FRIEND_PAY_SELECTORS.CHAR_COUNT);

    if (changeMessageLink && !changeMessageLink.dataset.listenerAdded) {
      changeMessageLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (textarea) {
          textarea.value = '';
          textarea.focus();
          if (charCount) {
            charCount.textContent = '0';
          }
        }
      });
      changeMessageLink.dataset.listenerAdded = 'true';
    }
  }

  /**
   * Setup share button handlers
   * @private
   * @param {HTMLElement} container - Sheet container element
   */
  _setupShareButtons(container) {
    const textarea = container.querySelector(FRIEND_PAY_SELECTORS.TEXTAREA);
    const shareButtons = container.querySelectorAll(FRIEND_PAY_SELECTORS.SHARE_BUTTONS);

    shareButtons.forEach(btn => {
      if (!btn.dataset.listenerAdded) {
        btn.addEventListener('click', () => {
          const shareType = btn.dataset.shareType;
          const message = textarea ? textarea.value : '';
          this.handleShare(shareType, message);
        });
        btn.dataset.listenerAdded = 'true';
      }
    });
  }

  /**
   * Setup main sheet action buttons (Send to Friend, Change Payment, Update)
   * @private
   * @param {HTMLElement} container - Sheet container element
   * @param {Object} handlers - Handler functions
   */
  _setupMainSheetActions(container, handlers) {
    // Send to friend button
    const sendToFriendBtn = container.querySelector(FRIEND_PAY_SELECTORS.SEND_TO_FRIEND_BTN);
    if (sendToFriendBtn && !sendToFriendBtn.dataset.listenerAdded) {
      sendToFriendBtn.addEventListener('click', () => {
        handlers.onSendToFriend();
      });
      sendToFriendBtn.dataset.listenerAdded = 'true';
    }

    // Change payment method button
    const changePaymentBtn = container.querySelector(FRIEND_PAY_SELECTORS.CHANGE_PAYMENT_BTN);
    if (changePaymentBtn && !changePaymentBtn.dataset.listenerAdded) {
      changePaymentBtn.addEventListener('click', () => {
        handlers.onChangePayment();
      });
      changePaymentBtn.dataset.listenerAdded = 'true';
    }

    // Update payment status button
    const updateBtn = container.querySelector(FRIEND_PAY_SELECTORS.UPDATE_BTN);
    if (updateBtn && !updateBtn.dataset.listenerAdded) {
      updateBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        await handlers.onUpdate();
      });
      updateBtn.dataset.listenerAdded = 'true';
    }
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    this.currentSheet = null;
  }
}

module.exports = FriendPayService;
